/* Super-admin login page — /super-admin-login
   ─────────────────────────────────────────────────────────────────────
   Passkey (WebAuthn) primary login + recovery-code fallback link.
*/
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    ShieldCheck, Fingerprint, ArrowRight, LifebuoyIcon,
} from "@phosphor-icons/react";

export default function SuperAdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [showRecovery, setShowRecovery] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState("");

    const passkeyLogin = async () => {
        if (!email.trim()) {
            toast.error("Enter your super-admin email");
            return;
        }
        setBusy(true);
        try {
            const opts = await api.post("/super-admin/login/challenge", { email: email.trim().toLowerCase() });
            const assertion = await startAuthentication({ optionsJSON: opts.data.options });
            const verify = await api.post("/super-admin/login/verify", { credential: assertion });
            localStorage.setItem("ca_token", verify.data.token);
            toast.success(`Welcome back, ${verify.data.user.name}`);
            navigate("/super-admin");
        } catch (err) {
            const msg = err?.name === "NotAllowedError"
                ? "Passkey prompt was cancelled — try again"
                : (err.response?.data?.detail || err.message || "Login failed");
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const recoveryLogin = async () => {
        if (!email.trim() || !recoveryCode.trim()) {
            toast.error("Enter your email and one recovery code");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/super-admin/login/recovery", {
                email: email.trim().toLowerCase(),
                code: recoveryCode.trim().toUpperCase(),
            });
            localStorage.setItem("ca_token", r.data.token);
            toast.success("Recovery code accepted — please add a new passkey");
            navigate(r.data.must_add_passkey ? "/super-admin/security?add_passkey=1" : "/super-admin");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Recovery failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-2 mb-6">
                    <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/30 grid place-items-center text-primary">
                        <ShieldCheck size={20} weight="fill" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">Invisique™</div>
                        <div className="text-sm font-semibold">Super Admin</div>
                    </div>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8" data-testid="sa-login-card">
                    {!showRecovery && (
                        <>
                            <h1 className="text-2xl font-display font-semibold mb-1">Login with Passkey</h1>
                            <p className="text-sm text-slate-400 mb-6">Phishing-resistant. No password, no SMS.</p>
                            <Label className="text-xs font-semibold text-slate-300">Super-admin email</Label>
                            <Input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@invisique.com"
                                className="mt-1 mb-4 bg-slate-950 border-slate-700 text-slate-100"
                                data-testid="sa-login-email"
                                autoFocus
                                onKeyDown={(e) => e.key === "Enter" && passkeyLogin()}
                            />
                            <Button
                                className="w-full bg-primary hover:bg-primary/90"
                                onClick={passkeyLogin} disabled={busy}
                                data-testid="sa-login-passkey-btn">
                                {busy ? "Waiting for your device…" : "Continue with passkey"}
                                <Fingerprint size={14} weight="bold" className="ml-1.5" />
                            </Button>
                            <button
                                type="button"
                                className="w-full mt-4 text-xs text-slate-400 hover:text-slate-200 transition-colors flex items-center justify-center gap-1"
                                onClick={() => setShowRecovery(true)}
                                data-testid="sa-login-recovery-link">
                                <LifebuoyIcon size={12} weight="duotone" />
                                Lost every device? Use a recovery code
                            </button>
                        </>
                    )}
                    {showRecovery && (
                        <>
                            <h1 className="text-xl font-display font-semibold mb-1">Recovery code login</h1>
                            <p className="text-sm text-slate-400 mb-6">Burns one of your 10 codes. You&apos;ll be forced to add a new passkey immediately.</p>
                            <Label className="text-xs font-semibold text-slate-300">Email</Label>
                            <Input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@invisique.com"
                                className="mt-1 mb-3 bg-slate-950 border-slate-700 text-slate-100"
                                data-testid="sa-recovery-email"
                            />
                            <Label className="text-xs font-semibold text-slate-300">Recovery code</Label>
                            <Input
                                value={recoveryCode}
                                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                                placeholder="4K2M9-XR3T7-P8QW1D"
                                className="mt-1 mb-4 bg-slate-950 border-slate-700 text-slate-100 font-mono tracking-widest"
                                data-testid="sa-recovery-code"
                            />
                            <Button
                                className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                                onClick={recoveryLogin} disabled={busy}
                                data-testid="sa-recovery-submit-btn">
                                {busy ? "Verifying…" : "Burn code + log in"}
                                <ArrowRight size={14} weight="bold" className="ml-1.5" />
                            </Button>
                            <button
                                type="button"
                                className="w-full mt-4 text-xs text-slate-400 hover:text-slate-200 transition-colors"
                                onClick={() => setShowRecovery(false)}
                                data-testid="sa-recovery-cancel">
                                ← Back to passkey login
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
